// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/cart.ts
// 🔧 PURPOSE: IL CARRELLO degli extra: più servizi insieme, un pagamento
//             solo. Una riga per servizio (rimettere lo stesso servizio
//             sostituisce la riga: quantità e varianti si cambiano nella
//             scheda). Con il channel manager nuovo (`multiItemLines`) i
//             servizi ad articoli hanno una riga per ARTICOLO: spazzolino e
//             dentifricio stanno insieme. Vive in localStorage, legato alla
//             prenotazione: con una prenotazione diversa si svuota da solo.
//
//             Il prezzo delle righe è solo l'ANTEPRIMA: il server riprezza
//             ogni articolo dal listino quando si paga.
// ============================================

import { useCallback, useSyncExternalStore } from 'react';
import type { PaySheetPayload } from './extraSelection';

export type CartLine = PaySheetPayload & {
  /**
   * La chiave della riga: il servizio, oppure "servizio:articolo" quando il
   * pagamento accetta più articoli dello stesso servizio. Assente (righe
   * salvate prima) = il servizio.
   */
  key?: string;
};

/** La chiave di una riga del carrello. */
export const cartLineKey = (line: Pick<CartLine, 'serviceId' | 'key'>): string => line.key || line.serviceId;

const itemOf = (line: Pick<CartLine, 'details'>): string => String((line.details && line.details.itemVariant) || '');

/**
 * La chiave di una riga nuova: "servizio:articolo" se il pagamento accetta
 * più righe dello stesso servizio (`byItem`) e la riga ha un articolo,
 * altrimenti il servizio (una riga sola, come sempre).
 */
export function lineKeyFor(line: PaySheetPayload, byItem: boolean): string {
  const item = itemOf(line);
  return byItem && item ? `${line.serviceId}:${item}` : line.serviceId;
}

interface CartState {
  owner: string;
  lines: CartLine[];
}

const STORAGE_KEY = 'nva_cart_v1';
const EMPTY: CartState = { owner: '', lines: [] };

let state: CartState = read();
const listeners = new Set<() => void>();

function read(): CartState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as CartState;
    if (!parsed || !Array.isArray(parsed.lines)) return EMPTY;
    return { owner: String(parsed.owner || ''), lines: parsed.lines };
  } catch {
    return EMPTY;
  }
}

function write(next: CartState): void {
  state = next;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* storage non disponibile */ }
  listeners.forEach((fn) => fn());
}

const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const getSnapshot = () => state;

export const cartStore = {
  /** Le righe di QUESTA prenotazione; un'altra prenotazione trova il carrello vuoto. */
  linesFor(owner: string): CartLine[] {
    return state.owner === owner ? state.lines : [];
  },
  /**
   * Mette una riga. Senza `byItem` sostituisce la riga dello stesso servizio
   * (quantità e varianti si cambiano nella scheda); con `byItem` sostituisce
   * solo quella dello stesso ARTICOLO, e le altre del servizio restano.
   */
  add(owner: string, line: CartLine, opts: { byItem?: boolean } = {}): void {
    const base = state.owner === owner ? state.lines : [];
    const key = lineKeyFor(line, !!opts.byItem);
    const item = itemOf(line);
    const others = key === line.serviceId
      ? base.filter((l) => l.serviceId !== line.serviceId)
      : base.filter((l) => cartLineKey(l) !== key && !(l.serviceId === line.serviceId && itemOf(l) === item));
    write({ owner, lines: [...others, { ...line, key }] });
  },
  /** Toglie la riga con questa chiave (il servizio, per le righe di un servizio solo). */
  remove(owner: string, key: string): void {
    if (state.owner !== owner) return;
    write({ owner, lines: state.lines.filter((l) => cartLineKey(l) !== key) });
  },
  clear(): void {
    write({ owner: state.owner, lines: [] });
  },
};

export const cartTotal = (lines: CartLine[]): number =>
  Math.round(lines.reduce((sum, l) => sum + (Number(l.price) || 0), 0) * 100) / 100;

/** Il carrello della prenotazione in sessione (owner = riferimento prenotazione). */
export function useCart(owner: string | null | undefined) {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const key = owner || '';
  const lines = key && snapshot.owner === key ? snapshot.lines : [];
  const add = useCallback((line: CartLine, opts?: { byItem?: boolean }) => { if (key) cartStore.add(key, line, opts); }, [key]);
  const remove = useCallback((lineKey: string) => { if (key) cartStore.remove(key, lineKey); }, [key]);
  const clear = useCallback(() => cartStore.clear(), []);
  return { lines, count: lines.length, total: cartTotal(lines), add, remove, clear, has: (id: string) => lines.some((l) => l.serviceId === id) };
}
